// Housekeeping Board — Phase 2 hotel operational stack
import React, { useState, useEffect, useCallback } from 'react';import {
  Sparkles, Wrench, RefreshCw, Loader2, X, Plus, Check, AlertTriangle,
  BedDouble, User as UserIcon, Clock, Printer, Search, Activity,
} from 'lucide-react';
import InstallAppButton from './InstallAppButton';
import PushNotificationButton from './PushNotificationButton';
import LostFoundPanel from './LostFoundPanel';
import { BeforeAfterStrip, TicketPhotoPicker, TicketPhotoStrip, photosByPhase, uploadTicketPhotos } from './TicketPhotos';
import ResolveTicketModal from './ResolveTicketModal';
import { HkPlanCard } from './GrowthCards';
import { apiCall } from './_shared';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const T = {
  en: {
    title: "Housekeeping", refresh: "Refresh", tickets: "Maintenance tickets",
    newTicket: "New ticket", issue: "Issue", priority: "Priority",
    assignedTo: "Assigned to", noTickets: "No open tickets", resolve: "Resolve",
    resolution: "Resolution", room: "Room", cancel: "Cancel", save: "Save",
    low: "Low", normal: "Normal", high: "High",
    clean: "Clean", cleaning: "Cleaning…", dirty: "Dirty", to_repair: "To repair",
    inspected: "Inspected",
    occupied: "Occupied", ooo: "Out of order",
    changeStatus: "Change room status", lastCleaned: "Last cleaned",
    createTicket: "Create maintenance ticket", statusLabel: "Status",
    handoff: "Handoff PDF", servicedToday: "Serviced today",
    markServiced: "Mark serviced", undoService: "Undo",
    sheetsToday: "Sheets today", vacantLabel: "Vacant",
    search: "Search room # or name…", noMatch: "No rooms match",
    activity: "Live activity", noActivity: "No activity yet",
    checkOut: "Check-out", nights: "n", guestNow: "Current guest",
    checkoutAt: "Checkout time", noGuest: "Vacant — no active guest",
    reasonAutoCheckout: "auto after check-out",
    history: "Notes & history", noHistory: "No notes yet",
  },
  me: {
    title: "Održavanje", refresh: "Osvježi", tickets: "Prijave kvarova",
    newTicket: "Nova prijava", issue: "Problem", priority: "Prioritet",
    assignedTo: "Zaduženi", noTickets: "Nema otvorenih prijava", resolve: "Riješi",
    resolution: "Rješenje", room: "Soba", cancel: "Otkaži", save: "Sačuvaj",
    low: "Nizak", normal: "Normalan", high: "Visok",
    clean: "Čisto", cleaning: "Čišćenje…", dirty: "Prljavo", to_repair: "Za popravku",
    inspected: "Provjereno",
    occupied: "Zauzeto", ooo: "Van upotrebe",
    changeStatus: "Promijeni status sobe", lastCleaned: "Zadnje čišćenje",
    createTicket: "Kreiraj prijavu kvara", statusLabel: "Status",
    handoff: "PDF za smjenu", servicedToday: "Očišćeno danas",
    markServiced: "Označi kao očišćeno", undoService: "Poništi",
    sheetsToday: "Posteljina danas", vacantLabel: "Slobodno",
    search: "Pretraga sobe…", noMatch: "Nema poklapanja",
    activity: "Aktivnost uživo", noActivity: "Nema aktivnosti",
    checkOut: "Odjava",
    nights: "n", guestNow: "Trenutni gost",
    checkoutAt: "Vrijeme odjave", noGuest: "Slobodno — nema gosta",
    reasonAutoCheckout: "automatski nakon odjave",
    history: "Bilješke i istorija", noHistory: "Nema bilješki",
  },
  sq: {
    title: "Pastrimi", refresh: "Rifresko", tickets: "Defekt",
    newTicket: "Prit i ri", issue: "Problemi", priority: "Prioriteti",
    assignedTo: "Caktuar", noTickets: "Asnjë prit i hapur", resolve: "Zgjidh",
    resolution: "Zgjidhja", room: "Dhoma", cancel: "Anulo", save: "Ruaj",
    low: "I ulët", normal: "Normal", high: "I lartë",
    clean: "Pastër", cleaning: "Duke u pastruar…", dirty: "Pisët", to_repair: "Për riparim",
    inspected: "Kontrolluar",
    occupied: "Zënë", ooo: "Jashtë përdorimit",
    changeStatus: "Ndrysho statusin", lastCleaned: "Pastrim i fundit",
    createTicket: "Krijo prit mirëmbajtjeje", statusLabel: "Statusi",
    handoff: "PDF handoff", servicedToday: "Pastruar sot",
    markServiced: "Shëno pastruar", undoService: "Zhbë",
    sheetsToday: "Çarçafët sot", vacantLabel: "Bosh",
    search: "Kërko dhomën…", noMatch: "Asnjë dhomë",
    activity: "Aktivitet i drejtpërdrejtë", noActivity: "Ende asnjë aktivitet",
    checkOut: "Largim",
    nights: "n", guestNow: "Mysafiri aktual",
    checkoutAt: "Ora e largimit", noGuest: "Bosh — asnjë mysafir",
    reasonAutoCheckout: "automatikisht pas largimit",
    history: "Shënime & histori", noHistory: "Ende asnjë shënim",
  },
};

const STATUS_COLORS = {
  clean:     'bg-green-50 border-green-300 text-green-800',
  cleaning:  'bg-sky-50 border-sky-300 text-sky-800',
  dirty:     'bg-red-50 border-red-300 text-red-800',
  to_repair: 'bg-yellow-50 border-yellow-400 text-yellow-800',
  inspected: 'bg-blue-50 border-blue-300 text-blue-800',
  occupied:  'bg-orange-50 border-orange-300 text-orange-800',
  ooo:       'bg-gray-100 border-gray-400 text-gray-700',
};
// The 4 statuses the owner asked for as first-class options in the calendar +
// housekeeping board. Legacy inspected / occupied / ooo remain valid on the
// backend for edge cases but aren't shown in the primary pick UI.
const STATUS_ORDER = ['clean', 'cleaning', 'dirty', 'to_repair'];

const useApi = () => useCallback((path, opts = {}) => apiCall(path, opts), []);

const HousekeepingDashboard = ({ userRole, currentUser, initialView }) => {
  const api = useApi();
  const [view, setView] = useState(initialView === 'lostfound' ? 'lostfound' : 'board');
  useEffect(() => { if (initialView === 'lostfound') setView('lostfound'); }, [initialView]);
  const [floorSel, setFloorSel] = useState('all');
  const myFloors = (currentUser?.floors || []).map(String);
  const [uiLang, setUiLang] = useState(() => localStorage.getItem('reception_lang') || 'en');
  const t = T[uiLang] || T.en;
  const {
    title, refresh: L_refresh, tickets: L_tickets, newTicket, issue: L_issue,
    priority: L_priority, assignedTo, noTickets, resolve: L_resolve, resolution: L_resolution,
    room: L_room, cancel: L_cancel, save: L_save, low: L_low, normal: L_normal, high: L_high,
    changeStatus, lastCleaned, createTicket: L_createTicket, statusLabel,
  } = t;

  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState({ rooms: [], counts: {} });
  const [ticketsData, setTicketsData] = useState({ tickets: [], counts: {} });
  const [handoff, setHandoff] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nt, setNt] = useState({ room_id: '', issue: '', priority: 'normal', assigned_to: '' });
  const [ntPhotos, setNtPhotos] = useState([]);
  const [ntBusy, setNtBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [activity, setActivity] = useState([]);
  const [history, setHistory] = useState({ unit_key: null, items: [], loading: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, tk, hf, ac] = await Promise.all([
        api('/api/admin/housekeeping/status'),
        api('/api/admin/housekeeping/tickets'),
        api('/api/admin/housekeeping/handoff').catch(() => null),
        api('/api/admin/housekeeping/activity?limit=20').catch(() => ({ items: [] })),
      ]);
      setBoard(b); setTicketsData(tk); setHandoff(hf); setActivity(ac.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh activity every 30s so the team sees fresh events without
  // manually pressing Refresh.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const ac = await api('/api/admin/housekeeping/activity?limit=20');
        setActivity(ac.items || []);
      } catch (e) { /* silently ignore */ }
    }, 30000);
    return () => clearInterval(t);
  }, [api]);

  // Lazy-load the per-unit notes timeline when the Mobile Room Sheet opens.
  useEffect(() => {
    if (!selectedRoom) {
      setHistory({ unit_key: null, items: [], loading: false });
      return;
    }
    const key = selectedRoom.unit_key;
    setHistory({ unit_key: key, items: [], loading: true });
    (async () => {
      try {
        const h = await api(`/api/admin/housekeeping/unit/${key}/history?limit=20`);
        // Guard against a race where the user opens a different unit while
        // this promise is in-flight.
        setHistory((prev) => prev.unit_key === key ? { unit_key: key, items: h.items || [], loading: false } : prev);
      } catch (e) {
        setHistory((prev) => prev.unit_key === key ? { unit_key: key, items: [], loading: false } : prev);
      }
    })();
  }, [selectedRoom, api]);

  const setStatus = async (unitKey, status) => {
    await api(`/api/admin/housekeeping/status/unit/${unitKey}`, { method: 'PUT', body: JSON.stringify({ status }) });
    setSelectedRoom(null);
    load();
  };

  const createTicket = async () => {
    if (!nt.issue || ntBusy) return;
    setNtBusy(true);
    try {
      const created = await api('/api/admin/housekeeping/tickets', {
        method: 'POST',
        body: JSON.stringify({
          room_id: nt.room_id ? parseInt(nt.room_id, 10) : null,
          room_name: nt.room_id ? (board.rooms.find(r => r.room_id === parseInt(nt.room_id, 10))?.room_name || '') : '',
          issue: nt.issue,
          priority: nt.priority,
          assigned_to: nt.assigned_to,
        }),
      });
      if (ntPhotos.length && created?.id) {
        const up = await uploadTicketPhotos(created.id, ntPhotos);
        if (up.errors.length) alert(up.errors.join('\n'));
      }
      setShowNew(false); setNt({ room_id: '', issue: '', priority: 'normal', assigned_to: '' }); setNtPhotos([]);
      load();
    } finally { setNtBusy(false); }
  };

  const [resolving, setResolving] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const resolveTicket = (id) => setResolving((ticketsData.tickets || []).find((x) => x.id === id) || null);

  const roomList = board.rooms || [];
  const allFloors = React.useMemo(() => {
    const set = new Set();
    for (const r of roomList) {
      const d = String(r.unit_name || '').replace(/\D/g, '');
      if (d.length >= 3) set.add(String(Math.floor(parseInt(d, 10) / 100)));
    }
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [roomList]);
  const openTickets = (ticketsData.tickets || []).filter(x => x.status !== 'resolved');
  const weekAgo = Date.now() - 7 * 86400000;
  const resolvedRecent = (ticketsData.tickets || []).filter(x => x.status === 'resolved' && x.resolved_at && new Date(x.resolved_at).getTime() >= weekAgo);
  const resolvedRows = resolvedRecent.map((tk) => {
    const ph = photosByPhase(tk.photos);
    return (
      <div key={tk.id} className="p-3 flex items-start gap-3" data-testid={`hk-resolved-${tk.id}`}>
        <Check className="w-5 h-5 text-green-600 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{tk.issue}</p>
          <p className="text-xs text-gray-500">{tk.unit_name ? `Room ${tk.unit_name} · ` : ''}{tk.room_name || ''} · resolved {String(tk.resolved_at).slice(0, 10)}{tk.updated_by ? ` by ${tk.updated_by}` : ''}</p>
          {tk.resolution && <p className="text-xs text-emerald-800 mt-0.5" data-testid={`hk-resolved-${tk.id}-note`}>{tk.resolution}</p>}
          <div className="mt-1"><BeforeAfterStrip before={ph.before} after={ph.after} testId={`hk-resolved-${tk.id}-photo`} /></div>
        </div>
      </div>
    );
  });
  const roomTypeOptions = React.useMemo(() => {
    const seen = new Map();
    for (const r of roomList) if (!seen.has(r.room_id)) seen.set(r.room_id, r.room_name);
    return [...seen.entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>);
  }, [roomList]);
  const counts = board.counts || {};

  // Build a quick lookup for handoff enrichments (sheet_change + serviced_today
  // + current guest) so each unit card can render its extra badges without a
  // per-card re-fetch. Keyed by `unit_key` now that each room type expands
  // into multiple physical units.
  const handoffByUnit = React.useMemo(() => {
    if (!handoff) return {};
    const map = {};
    for (const bucket of ['departures', 'stayovers', 'arrivals']) {
      for (const r of handoff[bucket] || []) {
        if (r.unit_key) map[r.unit_key] = { ...r, bucket };
      }
    }
    return map;
  }, [handoff]);

  const toggleServiced = async (unitKey, serviced) => {
    try {
      await api(`/api/admin/housekeeping/service/unit/${unitKey}`, {
        method: serviced ? 'DELETE' : 'POST',
      });
      load();
    } catch (e) { alert(e.message); }
  };

  // Departures-first sort: within each room-type, units checking out today
  // bubble to the front so cleaners see the fastest-turnover rooms first.
  const sortedRoomList = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...roomList];
    if (q) {
      list = list.filter((r) => {
        const num = (r.unit_name || `${r.room_id}.${r.unit_number}`).toString().toLowerCase();
        const name = (r.room_name || '').toLowerCase();
        return num.includes(q) || name.includes(q);
      });
    }
    list.sort((a, b) => {
      const ha = handoffByUnit[a.unit_key];
      const hb = handoffByUnit[b.unit_key];
      const aDep = ha?.bucket === 'departures' ? 0 : 1;
      const bDep = hb?.bucket === 'departures' ? 0 : 1;
      if (aDep !== bDep) return aDep - bDep;
      if (a.room_id !== b.room_id) return a.room_id - b.room_id;
      return a.unit_number - b.unit_number;
    });
    return list;
  }, [roomList, handoffByUnit, query]);

  return (
    <div className="space-y-6" data-testid="housekeeping-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-orange-500" /> {title}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{roomList.length} {L_room.toLowerCase()}s</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap justify-end max-w-full">
          <InstallAppButton lang={uiLang} />
          <PushNotificationButton role="housekeeping" lang={uiLang} />
          <select value={uiLang} onChange={(e) => { setUiLang(e.target.value); localStorage.setItem('reception_lang', e.target.value); }} className="px-3 py-1.5 border rounded text-sm" data-testid="hk-lang-picker">
            <option value="en">EN</option><option value="me">ME</option><option value="sq">SQ</option>
          </select>
          <button onClick={load} disabled={loading} className="flex items-center gap-2 bg-white border px-3 py-1.5 rounded text-sm hover:bg-gray-50" data-testid="hk-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {L_refresh}
          </button>
          <div className="flex items-stretch rounded overflow-hidden border border-orange-500" data-testid="hk-handoff-group">
            <select
              value={floorSel}
              onChange={(e) => setFloorSel(e.target.value)}
              className="px-2 text-xs bg-orange-50 text-orange-800 border-r border-orange-300 outline-none max-w-[120px]"
              title="Which floors to print"
              data-testid="hk-handoff-floors"
            >
              <option value="all">All floors</option>
              {myFloors.length > 0 && <option value="mine">My floors ({myFloors.join(', ')})</option>}
              {allFloors.map((f) => <option key={f} value={f}>Floor {f}</option>)}
            </select>
            <a
              href={`${API_URL}/api/admin/housekeeping/handoff.pdf?lang=${uiLang}${floorSel === 'mine' ? '&mine=1' : floorSel !== 'all' ? `&floors=${floorSel}` : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 text-sm font-semibold"
              data-testid="hk-handoff-pdf"
              title={t.handoff}
            >
              <Printer className="w-4 h-4" /> {t.handoff}
            </a>
          </div>
        </div>
      </div>

      <HkPlanCard canPublish={['super_admin', 'admin', 'manager', 'reception'].includes(userRole)} />

      <div className="flex gap-1 bg-white border rounded-lg p-1 w-fit" data-testid="hk-view-toggle">
        <button onClick={() => setView('board')} className={`px-3 py-1 rounded text-sm font-semibold ${view === 'board' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-testid="hk-view-board">Board</button>
        <button onClick={() => setView('lostfound')} className={`px-3 py-1 rounded text-sm font-semibold ${view === 'lostfound' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-testid="hk-view-lostfound">Lost &amp; Found</button>
      </div>

      {view === 'lostfound' && <LostFoundPanel userRole={userRole} />}
      {view === 'board' && <>

      {/* Status counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {STATUS_ORDER.map((s) => {
          const label = t[s];
          const count = counts[s] || 0;
          return (
            <div key={s} className={`rounded-lg border-2 p-3 ${STATUS_COLORS[s]}`} data-testid={`hk-count-${s}`}>
              <p className="text-xs font-medium">{label}</p>
              <p className="text-2xl font-bold">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Search + Activity feed row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white rounded-xl border p-3">
          <label className="flex items-center gap-2 border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-orange-300">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search}
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid="hk-room-search"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="text-gray-400 hover:text-gray-700"
                data-testid="hk-room-search-clear"
                aria-label="clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </label>
          <p className="text-[11px] text-gray-500 mt-1 pl-1">
            {sortedRoomList.length} / {roomList.length}
          </p>
        </div>
        <div
          className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl border border-slate-700 p-3 max-h-56 overflow-hidden flex flex-col"
          data-testid="hk-activity-feed"
        >
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-emerald-400" />
            <p className="text-xs font-bold uppercase tracking-wider">{t.activity}</p>
            <span className="ml-auto text-[10px] text-slate-400">{activity.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 text-[11px]">
            {activity.length === 0 ? (
              <p className="text-slate-500 italic">{t.noActivity}</p>
            ) : (
              activity.map((a) => {
                const roomLbl = a.unit_name
                  ? `Room ${a.unit_name}`
                  : (a.room_name || `Room ${a.room_id ?? '—'}`);
                const time = a.at ? new Date(a.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                const isCheckout = a.kind === 'checkout_auto_dirty';
                const iconClr = isCheckout ? 'text-red-400' : (a.to_status === 'clean' ? 'text-emerald-400' : 'text-sky-400');
                const arrow = isCheckout
                  ? `${t.checkOut} → dirty`
                  : `${a.from_status || '?'} → ${a.to_status || '?'}`;
                return (
                  <div key={a.id} className="flex items-start gap-2" data-testid={`hk-act-${a.id}`}>
                    <span className={`mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full ${iconClr.replace('text-', 'bg-')}`}></span>
                    <div className="flex-1 leading-tight">
                      <p className="text-slate-100">
                        <span className="font-semibold">{roomLbl}</span>
                        <span className="text-slate-400"> · {arrow}</span>
                      </p>
                      <p className="text-slate-500 text-[10px]">
                        {a.actor}{a.guest_name ? ` · ${a.guest_name}` : ''} · {time}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Unit grid — one card per physical room, grouped visually by type */}
      {sortedRoomList.length === 0 && query ? (
        <div className="bg-white border border-dashed rounded-xl p-8 text-center text-gray-500 text-sm" data-testid="hk-no-match">
          {t.noMatch}
        </div>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        {sortedRoomList.map((r) => {
          const cls = STATUS_COLORS[r.status] || STATUS_COLORS.clean;
          const label = t[r.status] || r.status;
          const lastCleanTxt = r.last_cleaned_at ? new Date(r.last_cleaned_at).toLocaleString() : '—';
          const hf = handoffByUnit[r.unit_key];
          const guestLine = hf?.guest_name || null;
          const unitLabel = r.unit_name
            ? `Room ${r.unit_name}`
            : (r.num_units > 1
                ? `Room ${r.room_id}.${r.unit_number}`
                : `Room ${r.room_id}`);
          return (
            <div
              key={r.unit_key}
              className={`rounded-lg border-2 p-3 min-h-[104px] hover:shadow-md transition-all touch-manipulation ${cls}`}
              data-testid={`hk-unit-${r.unit_key}`}
            >
              <button
                onClick={() => setSelectedRoom(r)}
                className="w-full text-left"
                data-testid={`hk-unit-open-${r.unit_key}`}
              >
                <div className="flex items-start justify-between">
                  <BedDouble className="w-5 h-5" />
                  <span className="text-[10px] font-bold uppercase tracking-wide">
                    {label}
                  </span>
                </div>
                <p className="font-bold text-sm leading-none mt-2">{unitLabel}</p>
                <p className="font-medium text-[11px] mt-0.5 leading-tight line-clamp-1 opacity-80">{r.room_name}</p>
                {guestLine ? (
                  <p className="text-[11px] mt-1 truncate flex items-center gap-1 font-medium">
                    <UserIcon className="w-3 h-3" /> {guestLine}
                  </p>
                ) : (
                  <p className="text-[11px] mt-1 truncate opacity-50">— {t.vacantLabel}</p>
                )}
                <p className="text-[10px] opacity-60 mt-0.5 truncate"><Clock className="w-3 h-3 inline mr-1" />{lastCleanTxt}</p>
              </button>

              {/* Badges + service toggle */}
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {hf?.bucket === 'departures' && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded"
                    data-testid={`hk-departure-${r.unit_key}`}
                    title="Check-out today — needs a fast turnover"
                  >
                    ↗ Check-out
                  </span>
                )}
                {hf?.sheet_change && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded"
                    data-testid={`hk-sheet-${r.unit_key}`}
                    title={t.sheetsToday}
                  >
                    🛏 {t.sheetsToday}
                  </span>
                )}
                {hf && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleServiced(r.unit_key, hf.serviced_today); }}
                    className={
                      hf.serviced_today
                        ? "inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-emerald-500 hover:bg-emerald-600 text-white px-1.5 py-0.5 rounded"
                        : "inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-white border border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-1.5 py-0.5 rounded"
                    }
                    data-testid={`hk-service-toggle-${r.unit_key}`}
                  >
                    <Check className="w-3 h-3" />
                    {hf.serviced_today ? t.servicedToday : t.markServiced}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Tickets */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2"><Wrench className="w-5 h-5 text-orange-500" /> {L_tickets} <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{openTickets.length}</span></h3>
          <button onClick={() => setShowNew(true)} className="flex items-center gap-2 bg-orange-500 text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-orange-600" data-testid="hk-new-ticket">
            <Plus className="w-4 h-4" /> {newTicket}
          </button>
        </div>
        <div className="divide-y">
          {openTickets.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-400">{noTickets}</p>
          ) : (
            openTickets.map((tk) => {
              const priCls = tk.priority === 'high' ? 'bg-red-100 text-red-700'
                : tk.priority === 'low' ? 'bg-gray-100 text-gray-600'
                : 'bg-yellow-100 text-yellow-700';
              return (
                <div key={tk.id} className="p-3 flex items-center gap-3" data-testid={`hk-ticket-${tk.id}`}>
                  <AlertTriangle className={`w-5 h-5 ${tk.priority === 'high' ? 'text-red-500' : 'text-yellow-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{tk.issue}</p>
                    <p className="text-xs text-gray-500">
                      {tk.unit_name ? `Room ${tk.unit_name} · ` : ''}{tk.room_name || `${L_room} #${tk.room_id || '—'}`}
                      {tk.assigned_to ? ` · ${assignedTo}: ${tk.assigned_to}` : ''}
                    </p>
                  </div>
                  <TicketPhotoStrip photos={tk.photos} phase="before" testId={`hk-ticket-${tk.id}-photo`} />
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${priCls}`}>{t[tk.priority] || tk.priority}</span>
                  <button onClick={() => resolveTicket(tk.id)} className="text-xs bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 flex items-center gap-1" data-testid={`hk-resolve-${tk.id}`}>
                    <Check className="w-3 h-3" /> {L_resolve}
                  </button>
                </div>
              );
            })
          )}
        </div>
        {resolvedRecent.length > 0 && (
          <div className="border-t">
            <button type="button" onClick={() => setShowResolved((v) => !v)} className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-2" data-testid="hk-resolved-toggle">
              <Check className="w-3.5 h-3.5 text-green-600" /> Resolved last 7 days · {resolvedRecent.length} {showResolved ? '▾' : '▸'}
            </button>
            {showResolved && <div className="divide-y bg-slate-50/50" data-testid="hk-resolved-list">{resolvedRows}</div>}
          </div>
        )}
      </div>
      {resolving && <ResolveTicketModal ticket={resolving} onClose={() => setResolving(null)} onDone={() => { setResolving(null); setShowResolved(true); load(); }} />}

      {/* Room sheet — mobile-first per-room detail with status actions */}
      {selectedRoom && (() => {
        const hfSel = handoffByUnit[selectedRoom.unit_key];
        const selLabel = selectedRoom.unit_name
          ? `Room ${selectedRoom.unit_name}`
          : (selectedRoom.num_units > 1
              ? `Room ${selectedRoom.room_id}.${selectedRoom.unit_number}`
              : `Room ${selectedRoom.room_id}`);
        return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelectedRoom(null)}>
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-2xl p-5 shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="hk-status-modal"
          >
            <div className="flex justify-between items-center mb-3">
              <div className="min-w-0">
                <h3 className="font-bold text-lg leading-tight truncate">{selLabel}</h3>
                <p className="text-xs text-gray-500 truncate">{selectedRoom.room_name}</p>
              </div>
              <button onClick={() => setSelectedRoom(null)} data-testid="hk-modal-close" className="shrink-0 ml-2 p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Guest sheet */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3 space-y-1" data-testid="hk-sheet-guest">
              {hfSel?.guest_name ? (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.guestNow}</p>
                  <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <UserIcon className="w-4 h-4" /> {hfSel.guest_name}
                    {typeof hfSel.adults === 'number' && (
                      <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                        {(hfSel.adults || 0) + (hfSel.children || 0)} pax
                      </span>
                    )}
                  </p>
                  {hfSel.check_in && hfSel.check_out && (
                    <p className="text-xs text-slate-600">
                      {hfSel.check_in} → <span className="font-semibold">{hfSel.check_out}</span>
                      {hfSel.bucket === 'departures' && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase font-bold bg-red-500 text-white px-1.5 py-0.5 rounded" data-testid="hk-sheet-checkout">
                          ↗ {t.checkOut}
                        </span>
                      )}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {hfSel.past_stays > 0 && (
                      <span className="text-[10px] font-bold uppercase bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid="hk-sheet-returning" title="Returning guest — see Guest Memory">
                        ↻ Returning · visit #{hfSel.past_stays + 1}
                      </span>
                    )}
                      <span className="text-[10px] font-bold uppercase bg-amber-500 text-white px-1.5 py-0.5 rounded" data-testid="hk-sheet-sheets">
                        🛏 {t.sheetsToday}
                      </span>
                    )}
                    {hfSel.serviced_today && (
                      <span className="text-[10px] font-bold uppercase bg-emerald-500 text-white px-1.5 py-0.5 rounded">
                        ✓ {t.servicedToday}
                      </span>
                    )}
                    {/* Guest tags — set from the calendar's guest history modal */}
                    {(hfSel.guest_tags || []).map((tg, i) => {
                      const cls = {
                        vip:      'bg-amber-500 text-amber-950',
                        quiet:    'bg-sky-500 text-white',
                        allergic: 'bg-red-500 text-white',
                        late:     'bg-purple-500 text-white',
                        pillow:   'bg-indigo-500 text-white',
                        note:     'bg-slate-600 text-white',
                      }[tg.kind] || 'bg-slate-600 text-white';
                      return (
                        <span
                          key={i}
                          className={`text-[10px] font-bold uppercase tracking-wider ${cls} px-1.5 py-0.5 rounded`}
                          data-testid={`hk-sheet-tag-${i}`}
                          title={`Guest tag · ${tg.kind}`}
                        >
                          {tg.label}
                        </span>
                      );
                    })}
                  </div>
                  {hfSel.unit_notes?.length > 0 && (
                    <p className="text-xs text-yellow-900 bg-yellow-50 border-l-4 border-yellow-400 rounded px-2 py-1 mt-1" data-testid="hk-sheet-unit-notes">
                      <span className="font-bold uppercase text-[10px] tracking-wider mr-1">Room note</span>{hfSel.unit_notes.join(' · ')}
                    </p>
                  )}
                  {hfSel.guest_preferences && (
                    <p className="text-xs text-indigo-900 bg-indigo-50 border-l-4 border-indigo-400 rounded px-2 py-1 mt-1" data-testid="hk-sheet-preferences">
                      <span className="font-bold uppercase text-[10px] tracking-wider mr-1">Prep</span>{hfSel.guest_preferences}
                    </p>
                  )}
                  {hfSel.special_requests && (
                    <p className="text-xs italic text-slate-600 pt-1">"{hfSel.special_requests}"</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-400 italic">{t.noGuest}</p>
              )}
            </div>

            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{changeStatus}</p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_ORDER.map((s) => (
                <button key={s} onClick={() => setStatus(selectedRoom.unit_key, s)}
                  className={`p-4 min-h-[60px] rounded-lg border-2 font-semibold text-sm ${STATUS_COLORS[s]} hover:opacity-80 active:scale-[0.97] touch-manipulation`}
                  data-testid={`hk-set-${s}`}>
                  {t[s]}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">
              {lastCleaned}: {selectedRoom.last_cleaned_at ? new Date(selectedRoom.last_cleaned_at).toLocaleString() : '—'}
            </p>

            {/* Notes & history timeline */}
            <div className="mt-4 pt-3 border-t" data-testid="hk-sheet-history">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> {t.history}
                {history.loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
              </p>
              {history.items.length === 0 && !history.loading ? (
                <p className="text-xs italic text-slate-400" data-testid="hk-sheet-no-history">{t.noHistory}</p>
              ) : (
                <ol className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {history.items.map((h) => {
                    const time = h.at ? new Date(h.at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    const isCheckout = h.kind === 'checkout_auto_dirty';
                    const dotClr = isCheckout
                      ? 'bg-red-400'
                      : (h.to_status === 'clean' ? 'bg-emerald-500'
                        : h.to_status === 'cleaning' ? 'bg-sky-500'
                        : h.to_status === 'dirty' ? 'bg-red-500'
                        : h.to_status === 'to_repair' ? 'bg-yellow-500'
                        : 'bg-slate-400');
                    const arrow = isCheckout
                      ? `${t.checkOut} → dirty`
                      : `${h.from_status || '?'} → ${h.to_status || '?'}`;
                    return (
                      <li key={h.id} className="flex items-start gap-2" data-testid={`hk-sheet-hist-${h.id}`}>
                        <span className={`mt-1 shrink-0 w-2 h-2 rounded-full ${dotClr}`}></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-800 leading-tight">
                            <span className="font-semibold">{arrow}</span>
                            <span className="text-slate-500"> · {h.actor}{h.guest_name ? ` · ${h.guest_name}` : ''}</span>
                          </p>
                          {h.note && (
                            <p className="text-xs italic text-slate-700 mt-0.5 break-words">"{h.note}"</p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-0.5">{time}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Create ticket modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="hk-new-ticket-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold">{L_createTicket}</h3>
              <button onClick={() => setShowNew(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <select value={nt.room_id} onChange={(e) => setNt({ ...nt, room_id: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="hk-ticket-room-select">
                <option value="">{L_room} —</option>
                {roomTypeOptions}
              </select>
              <input value={nt.issue} onChange={(e) => setNt({ ...nt, issue: e.target.value })} placeholder={L_issue} className="w-full px-3 py-2 border rounded text-sm" data-testid="hk-new-issue" />
              <div className="grid grid-cols-2 gap-2">
                <select value={nt.priority} onChange={(e) => setNt({ ...nt, priority: e.target.value })} className="px-3 py-2 border rounded text-sm">
                  <option value="low">{L_low}</option>
                  <option value="normal">{L_normal}</option>
                  <option value="high">{L_high}</option>
                </select>
                <input value={nt.assigned_to} onChange={(e) => setNt({ ...nt, assigned_to: e.target.value })} placeholder={assignedTo} className="px-3 py-2 border rounded text-sm" />
              </div>
              <TicketPhotoPicker files={ntPhotos} onChange={setNtPhotos} testId="hk-ticket-photos" />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">{L_cancel}</button>
                <button onClick={createTicket} disabled={!nt.issue || ntBusy} className="px-4 py-2 bg-orange-500 text-white rounded font-semibold text-sm disabled:opacity-50" data-testid="hk-new-save">{ntBusy ? '…' : L_save}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
};

export default HousekeepingDashboard;
